/**
 * Relatorios - o catalogo em si.
 *
 * Duas coisas que a tela precisa dizer, porque erram em silencio:
 *
 * - **Workspace do Auth e porta de acesso.** Por isso a coluna e os detalhes mostram o NOME do
 *   workspace, nao o GUID: "quem enxerga este relatorio?" e a pergunta que se faz aqui, e um GUID
 *   nao responde. Auth fora do ar cai no identificador com um aviso, em vez de derrubar a tela.
 * - **Publicado e o unico estado que aparece no Report Viewer.** Rascunho existe, esta correto, e
 *   nao e visto por ninguem.
 *
 * A edicao expoe TUDO que o `ReportUpdate` do backend aceita - inclusive o tipo do relatorio e os
 * campos do outro tipo. Esconder o que o backend aceita criava a duvida "por que nao da para mudar
 * isso aqui?"; o lugar de avisar do risco e a nota do campo, nao a ausencia dele.
 */

import { StatusBadge } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'

import { fetchConnections, fetchDatasets, fetchHospitals, fetchReports } from '../api/catalog'
import { fetchSnapshot } from '../api/onboarding'
import { ESTADOS_DE_PUBLICACAO, RESOURCE_CATALOGO } from '../api/types'
import type { Dataset, Hospital, PowerBiConnection, Report } from '../api/types'
import { fetchMe } from '../auth/api'
import { usePermissoes } from '../auth/permissoes'
import { Alert } from '../components/Alert'
import type { CampoMeta } from '../listagem/campos'
import { IdCurto, Listagem, Marca } from '../listagem/Listagem'
import { Resultado } from '../listagem/Resultado'

const TOM_DA_PUBLICACAO: Record<string, 'positive' | 'tech' | 'neutral'> = {
  published: 'positive',
  draft: 'tech',
  archived: 'neutral',
}

const ROTULO_DA_PUBLICACAO: Record<string, string> = {
  published: 'publicado',
  draft: 'rascunho',
  archived: 'arquivado',
}

interface Referencias {
  workspaces: Array<{ id: string; name: string }>
  hospitais: Hospital[]
  datasets: Dataset[]
  conexoes: PowerBiConnection[]
}

function campos(ref: Referencias): Array<CampoMeta<Report>> {
  const nomeDoWorkspace = new Map(ref.workspaces.map((item) => [item.id, item.name]))
  const nomeDoDataset = new Map(ref.datasets.map((item) => [item.id, item.name]))
  const nomeDaConexao = new Map(ref.conexoes.map((item) => [item.id, item.name]))
  const nomeDoHospital = new Map(ref.hospitais.map((item) => [item.id, item.name]))

  return [
    { nome: 'name', rotulo: 'Nome', grupo: 'Identificação', edicao: { tipo: 'texto' } },
    {
      nome: 'category',
      rotulo: 'Categoria',
      grupo: 'Identificação',
      edicao: { tipo: 'texto', opcional: true },
    },
    {
      nome: 'description',
      rotulo: 'Descrição',
      grupo: 'Identificação',
      edicao: { tipo: 'textarea', opcional: true },
    },
    {
      nome: 'report_type',
      rotulo: 'Tipo',
      grupo: 'Identificação',
      exibir: (item) => (
        <StatusBadge tone="neutral">
          {item.report_type === 'inteli_graph' ? 'Inteli Graph' : 'Power BI'}
        </StatusBadge>
      ),
      nota: 'Decide se o Viewer faz embed do Power BI ou abre a URL de render do Inteli Graph.',
      edicao: {
        tipo: 'selecao',
        dica: 'Trocar o tipo troca a forma de renderizar: confira os campos do tipo novo antes de salvar.',
        opcoes: () => [
          { valor: 'pbi', rotulo: 'Power BI' },
          { valor: 'inteli_graph', rotulo: 'Inteli Graph' },
        ],
      },
    },
    {
      nome: 'auth_workspace_id',
      rotulo: 'Workspace (Auth)',
      grupo: 'Acesso',
      exibir: (item) =>
        nomeDoWorkspace.get(item.auth_workspace_id) ?? <IdCurto valor={item.auth_workspace_id} />,
      nota: 'Porta de acesso: só quem alcança este workspace pelo profile vê o relatório.',
      edicao: {
        tipo: 'selecao',
        dica: 'Trocar aqui muda quem enxerga o relatório.',
        opcoes: () => ref.workspaces.map((ws) => ({ valor: ws.id, rotulo: ws.name })),
      },
    },
    {
      nome: 'hospital_id',
      rotulo: 'Hospital',
      grupo: 'Acesso',
      exibir: (item) =>
        item.hospital_id === null ? (
          <span className="vazio">—</span>
        ) : (
          (nomeDoHospital.get(item.hospital_id) ?? <IdCurto valor={item.hospital_id} />)
        ),
      nota: 'Opcional. Agrupa o relatório numa organização do Auth.',
      edicao: {
        tipo: 'selecao',
        opcional: true,
        opcoes: (item) =>
          ref.hospitais
            .filter((hospital) => hospital.client_id === item.client_id)
            .map((hospital) => ({ valor: hospital.id, rotulo: hospital.name })),
      },
    },
    {
      nome: 'powerbi_connection_id',
      rotulo: 'Conexão Power BI',
      grupo: 'Power BI',
      exibir: (item) =>
        item.powerbi_connection_id === null ? (
          <span className="vazio">—</span>
        ) : (
          (nomeDaConexao.get(item.powerbi_connection_id) ?? (
            <IdCurto valor={item.powerbi_connection_id} />
          ))
        ),
      edicao: {
        tipo: 'selecao',
        opcional: true,
        opcoes: (item) =>
          ref.conexoes
            .filter((conexao) => conexao.client_id === item.client_id)
            .map((conexao) => ({ valor: conexao.id, rotulo: conexao.name })),
      },
    },
    {
      nome: 'dataset_id',
      rotulo: 'Dataset',
      grupo: 'Power BI',
      exibir: (item) =>
        item.dataset_id === null ? (
          <span className="vazio">—</span>
        ) : (
          (nomeDoDataset.get(item.dataset_id) ?? <IdCurto valor={item.dataset_id} />)
        ),
      nota: 'Sem dataset não há RLS: a política filtra linhas no semantic model.',
      edicao: {
        tipo: 'selecao',
        opcional: true,
        dica: 'Trocar o dataset exige excluir as políticas de RLS do relatório antes.',
        opcoes: (item) =>
          ref.datasets
            .filter((dataset) => dataset.client_id === item.client_id)
            .map((dataset) => ({ valor: dataset.id, rotulo: dataset.name })),
      },
    },
    {
      nome: 'powerbi_workspace_id',
      rotulo: 'Workspace no Power BI (groupId)',
      grupo: 'Power BI',
      formato: 'identificador',
      edicao: { tipo: 'texto', opcional: true },
    },
    {
      nome: 'powerbi_report_id',
      rotulo: 'Relatório no Power BI',
      grupo: 'Power BI',
      formato: 'identificador',
      edicao: { tipo: 'texto', opcional: true },
    },
    {
      nome: 'embed_url',
      rotulo: 'Embed URL',
      grupo: 'Power BI',
      formato: 'url',
      nota: 'Obrigatória para publicar um relatório Power BI: é com ela que o Viewer renderiza.',
      edicao: { tipo: 'texto', opcional: true },
    },
    {
      nome: 'report_url',
      rotulo: 'URL de render',
      grupo: 'Inteli Graph',
      formato: 'url',
      nota: 'Só para relatórios do Inteli Graph. Obrigatória para publicar esse tipo.',
      edicao: { tipo: 'texto', opcional: true },
    },
    {
      nome: 'publication_status',
      rotulo: 'Publicação',
      grupo: 'Estado',
      exibir: (item) => (
        <StatusBadge tone={TOM_DA_PUBLICACAO[item.publication_status] ?? 'neutral'}>
          {ROTULO_DA_PUBLICACAO[item.publication_status] ?? item.publication_status}
        </StatusBadge>
      ),
      nota: 'Só publicado aparece no Report Viewer.',
      edicao: {
        tipo: 'selecao',
        opcoes: () =>
          ESTADOS_DE_PUBLICACAO.map((estado) => ({
            valor: estado,
            rotulo: ROTULO_DA_PUBLICACAO[estado] ?? estado,
          })),
      },
    },
    {
      nome: 'is_active',
      rotulo: 'Relatório ativo',
      grupo: 'Estado',
      formato: 'booleano',
      edicao: { tipo: 'booleano' },
    },
    { nome: 'client_slug', rotulo: 'Cliente', grupo: 'Registro', formato: 'codigo' },
    { nome: 'hospital_slug', rotulo: 'Hospital (slug)', grupo: 'Registro', formato: 'codigo' },
    {
      nome: 'client_id',
      rotulo: 'Cliente (identificador)',
      grupo: 'Registro',
      formato: 'identificador',
    },
    { nome: 'id', rotulo: 'Identificador no catálogo', grupo: 'Registro', formato: 'identificador' },
  ]
}

export function RelatoriosPage() {
  const { pode } = usePermissoes()
  const relatorios = useQuery({ queryKey: ['reports'], queryFn: fetchReports })
  const hospitais = useQuery({ queryKey: ['hospitals'], queryFn: fetchHospitals })
  const datasets = useQuery({ queryKey: ['datasets'], queryFn: fetchDatasets })
  const conexoes = useQuery({ queryKey: ['powerbi-connections'], queryFn: fetchConnections })
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const tenantId = me.data?.tenant_id ?? ''
  const snapshot = useQuery({
    queryKey: ['snapshot', tenantId],
    queryFn: () => fetchSnapshot(tenantId),
    enabled: tenantId !== '',
    staleTime: 30 * 60 * 1000,
  })

  const referencias: Referencias = {
    workspaces: snapshot.data?.workspaces.map((ws) => ({ id: ws.id, name: ws.name })) ?? [],
    hospitais: hospitais.data ?? [],
    datasets: datasets.data ?? [],
    conexoes: conexoes.data ?? [],
  }
  const nomeDoWorkspace = new Map(referencias.workspaces.map((item) => [item.id, item.name]))

  return (
    <>

      {snapshot.error === null ? null : (
        <Alert tone="warning">
          O Auth não respondeu: os nomes de workspace aparecem como identificador e a lista de
          escolha fica vazia na edição. O resto da tela continua correto.
        </Alert>
      )}

      <Resultado consulta={relatorios}>
        {(lista) => (
          <Listagem<Report>
            itens={lista}
            chave="reports"
            entidade="reports"
            rotulo="relatório"
            rotuloPlural="relatórios"
            campos={campos(referencias)}
            podeGerenciar={pode(RESOURCE_CATALOGO, 'manage')}
            idDe={(item) => item.id}
            nomeDe={(item) => item.name}
            colunas={[
              { titulo: 'Nome', valor: (item) => item.name },
              {
                titulo: 'Tipo',
                valor: (item) => (
                  <StatusBadge tone="neutral">
                    {item.report_type === 'inteli_graph' ? 'NIG' : 'Power BI'}
                  </StatusBadge>
                ),
              },
              { titulo: 'Categoria', valor: (item) => item.category ?? '—' },
              {
                titulo: 'Workspace',
                valor: (item) =>
                  nomeDoWorkspace.get(item.auth_workspace_id) ?? (
                    <IdCurto valor={item.auth_workspace_id} />
                  ),
              },
              {
                titulo: 'Publicação',
                valor: (item) => (
                  <StatusBadge tone={TOM_DA_PUBLICACAO[item.publication_status] ?? 'neutral'}>
                    {ROTULO_DA_PUBLICACAO[item.publication_status] ?? item.publication_status}
                  </StatusBadge>
                ),
              },
              { titulo: 'Ativo', valor: (item) => <Marca valor={item.is_active} /> },
            ]}
            avisoDeEdicao="Publicar exige conexão, dataset, workspace do Power BI, id do relatório e embed URL preenchidos — o backend recusa a publicação sem eles."
            vazio="Nenhum relatório cadastrado. Crie no onboarding, passo 5."
          />
        )}
      </Resultado>
    </>
  )
}
